import { useState } from "react";
import { Link } from "react-router-dom";
import { Bot, ChevronDown, LogOut, Shield, Sparkles, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { APP_VERSION_DISPLAY } from "@/config/version";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { HIGHEST_PLAN_ID } from "@/config/plans";
import { NavCreditsDisplay } from "./NavCreditsDisplay";
import type { UserDisplayInfo } from "./types";

interface NavUserMenuProps {
  userDisplayInfo: UserDisplayInfo;
  credits: number;
  maxCredits: number;
  creditsPercentage: number;
  isAdmin: boolean;
  handleSignOut: () => Promise<void>;
  t: {
    nav: {
      plan: string;
      credits: string;
      remaining: string;
      upgradePlan: string;
      managePlan: string;
      myProfile: string;
      myBot: string;
      betaSection: string;
      admin: string;
      logout: string;
    };
  };
}

export function NavUserMenu({
  userDisplayInfo,
  credits,
  maxCredits,
  creditsPercentage,
  isAdmin,
  handleSignOut,
  t,
}: NavUserMenuProps) {
  const { displayName, planName, avatarUrl } = userDisplayInfo;
  const [open, setOpen] = useState(false);
  // Beta section starts collapsed so the user menu stays compact; users opt
  // in to see experimental features by expanding it. Future beta items live
  // inside the same group, so adding one is a single Link inside the panel.
  const [betaOpen, setBetaOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-border hover:border-primary transition-colors cursor-pointer flex-shrink-0">
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-4 bg-card border-border z-[60]"
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-primary/30">
              <img
                src={avatarUrl}
                alt={displayName}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-bold text-foreground">{displayName}</p>
              <p className="text-xs text-muted-foreground capitalize">
                {t.nav.plan} {planName}
              </p>
            </div>
          </div>

          <NavCreditsDisplay
            credits={credits}
            maxCredits={maxCredits}
            creditsPercentage={creditsPercentage}
            creditsLabel={t.nav.credits}
            remainingLabel={t.nav.remaining}
          />

          <div className="flex flex-col gap-2">
            <Link to="/pricing" onClick={() => setOpen(false)}>
              <button className="w-full py-2.5 rounded-xl font-semibold text-sm text-primary-foreground bg-primary hover:bg-primary/90 transition-all">
                {userDisplayInfo.planName === HIGHEST_PLAN_ID ? t.nav.managePlan : t.nav.upgradePlan}
              </button>
            </Link>
            <Link to="/profile" onClick={() => setOpen(false)}>
              <button className="w-full py-2.5 rounded-xl font-medium text-sm text-foreground hover:bg-accent border border-border transition-all flex items-center justify-center gap-2">
                <User className="w-4 h-4" />
                {t.nav.myProfile}
              </button>
            </Link>
            {/* Beta section — collapsible group for experimental features.
                Trigger matches the other menu buttons (border, padding,
                font); when expanded, sub-items reuse the exact same style
                so the panel stays visually uniform. */}
            <button
              type="button"
              onClick={() => setBetaOpen((v) => !v)}
              className="relative w-full py-2.5 rounded-xl font-medium text-sm text-foreground hover:bg-accent border border-border transition-all flex items-center justify-center gap-2"
              aria-expanded={betaOpen}
            >
              <Sparkles className="w-4 h-4 text-primary" />
              {t.nav.betaSection}
              <ChevronDown
                className={cn(
                  "absolute end-3 w-4 h-4 text-muted-foreground transition-transform",
                  betaOpen && "rotate-180",
                )}
              />
            </button>
            {betaOpen && (
              <Link to="/bot-builder" onClick={() => setOpen(false)}>
                <button className="w-full py-2.5 rounded-xl font-medium text-sm text-foreground hover:bg-accent border border-border transition-all flex items-center justify-center gap-2">
                  <Bot className="w-4 h-4" />
                  {t.nav.myBot}
                </button>
              </Link>
            )}
            {isAdmin && (
              <Link to="/admin" onClick={() => setOpen(false)}>
                <button className="w-full py-2.5 rounded-xl font-medium text-sm text-foreground hover:bg-accent border border-border transition-all flex items-center justify-center gap-2">
                  <Shield className="w-4 h-4" />
                  {t.nav.admin}
                </button>
              </Link>
            )}
            <button
              onClick={() => { setOpen(false); handleSignOut(); }}
              className="w-full py-2.5 rounded-xl font-medium text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border transition-all flex items-center justify-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              {t.nav.logout}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground/50 text-center pt-1">
            {APP_VERSION_DISPLAY}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
